import {ChangeDetectionStrategy, Component, forwardRef, Input} from '@angular/core';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {DateFormatPipe, TimeUnlocalizePipe} from '@theme/pipes';
import {Unsubscribable} from '@core/interfaces/unsubscribable';
import {FormatsService} from '@core/utils';
import {NbCalendarRange} from '@nebular/theme/components/calendar/calendar-range.component';

export interface DateRangeValue extends NbCalendarRange<Date> {}

@Component({
    selector: 'ngx-date-range-picker',
    templateUrl: './date-range-picker.component.html',
    styleUrls: ['./date-range-picker.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DateRangePickerComponent),
            multi: true,
        },
    ],
})
export class DateRangePickerComponent extends Unsubscribable implements ControlValueAccessor {
    @Input() convertDateTimezone: boolean = true; // Convert entered date from local timezone to UTC
    @Input() dateWithTime: boolean = false; // Value is a datetime, false means value is a date

    constructor(private dateFormatPipe: DateFormatPipe, private unlocalizeTimePipe: TimeUnlocalizePipe) {
        super();
    }

    dateRange: DateRangeValue = null;

    // Implement parent methods
    onChange = (value: DateRangeValue) => {};
    onTouched = () => {};
    writeValue(value: DateRangeValue): void {
        this.dateRange = value;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    delay = 500; // milliseconds
    inputControl: FormControl = new FormControl();

    /** Triggers at start and end of date selection. Need to make sure we don't clear partial dates */
    dateRangeChange(value: DateRangeValue): void {
        // Ensure no filter is being applied; note: don't clear partial dates
        if (value == null || (value.start == null && value.end == null)) {
            this.dateRange = {
                start: null,
                end: null,
            };
            this.onChange(this.dateRange);
        }

        if (!!value && value.start && value.end) {
            let startDateRaw: Date, endDateRaw: Date, format: string;

            if (this.dateWithTime) {
                format = FormatsService.PROPER_DATETIME_FORMAT;
                startDateRaw = this.dateFormatPipe.transform(value.start, FormatsService.PROPER_DATETIME_FORMAT);
                endDateRaw = this.dateFormatPipe.transform(value.end, FormatsService.PROPER_DATETIME_FORMAT);
            } else {
                format = FormatsService.PROPER_DATE_FORMAT;
                startDateRaw = this.dateFormatPipe.transform(value.start, FormatsService.PROPER_DATE_FORMAT);
                // End date is set to selected date + 1 day (i.e. 12:00am next day); includes entire end day.
                endDateRaw = this.dateFormatPipe.transform(value.end, FormatsService.PROPER_DATE_FORMAT).add(1, 'days');
            }

            // Optional convert from local to UTC timezone
            const startDate = this.convertDateTimezone
                ? this.unlocalizeTimePipe.transform(startDateRaw, format)
                : startDateRaw;
            const endDate = this.convertDateTimezone
                ? this.unlocalizeTimePipe.transform(endDateRaw, format)
                : endDateRaw;

            // Set final value
            this.dateRange = {
                start: startDate,
                end: endDate,
            };
            this.onChange(this.dateRange);
        }
    }

    clearDateFilterInput() {
        // Propagate a change to the field
        this.dateRangeChange({start: null, end: null});
        this.inputControl.reset();
    }
}
